import { Card, CardContent } from "@/components/ui/card"
import { formatMoney } from "@/lib/formatters"
import { cn } from "@/lib/utils"
import { toneDotClass, type StatusTone } from "@/lib/status-colors"
import { Sparkline } from "@/features/staff/stats/sparkline"
import {
  formatGrowth,
  formatShare,
  growthTone,
  tStats,
  type GrowthTone,
} from "@/features/staff/stats/format"
import type { Stats, StatsBucket } from "@/features/staff/stats/types"

/**
 * The four figures the screen opens on.
 *
 * Every one of them is over the CLOSED buckets. The bucket in progress is drawn
 * in the chart below and counted in none of these, which is the whole reason
 * this screen can be compared against anything at all.
 *
 * The dot carries the direction and the figure never does — `text-foreground
 * font-semibold tabular-nums`, always. That is the design system's dot rule, and
 * it exists because a percentage rendered in red at `text-sm` is already below
 * contrast on a tinted surface while the sign already said which way.
 */
const GROWTH_TONE: Record<GrowthTone, StatusTone> = {
  up: "success",
  down: "danger",
  flat: "disabled",
  new: "reserved",
}

function Kpi({
  label,
  value,
  note,
  tone,
  delta,
  buckets,
  testId,
}: {
  label: string
  value: string
  note?: string
  tone?: StatusTone
  delta?: string
  buckets?: StatsBucket[]
  testId: string
}) {
  return (
    <Card density="compact" className="gap-2">
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className="flex items-end justify-between gap-3">
          <p className="text-lg font-semibold tabular-nums text-foreground" data-testid={testId}>
            {value}
          </p>
          {buckets ? <Sparkline buckets={buckets} /> : null}
        </div>
        {delta ? (
          <div className="flex items-center gap-2">
            <span aria-hidden="true" className={cn("shrink-0", toneDotClass(tone ?? "disabled", "size-2"))} />
            <span className="text-sm font-semibold tabular-nums text-foreground">{delta}</span>
            {note ? <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{note}</span> : null}
          </div>
        ) : note ? (
          <p className="text-xs text-muted-foreground">{note}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function StatsKpiBand({ stats }: { stats: Stats }) {
  const { sales, mix } = stats
  const closed = stats.series.filter((bucket) => !bucket.open)

  // When the two sides of the comparison do not bracket the same number of
  // closed shifts, the honest delta is the per-shift one and the card says which
  // it is showing. Same rule, same wording as the period report's strip.
  const normalized = sales.normalizeOnAverage
  const ratio = normalized ? sales.perShiftGrowthRatio : sales.growthRatio

  return (
    <section
      className="grid grid-cols-2 gap-4 xl:grid-cols-4"
      data-testid="stats-kpi-band"
    >
      <Kpi
        testId="stats-kpi-net-sales"
        label={tStats("kpi.net_sales")}
        value={formatMoney(sales.subtotal)}
        buckets={closed}
        tone={GROWTH_TONE[growthTone(ratio)]}
        delta={formatGrowth(ratio)}
        note={
          normalized
            ? tStats("kpi.normalized")
            : sales.previous
              ? tStats("kpi.versus", { amount: formatMoney(sales.previous.subtotal) })
              : undefined
        }
      />
      <Kpi
        testId="stats-kpi-average-ticket"
        label={tStats("kpi.average_ticket")}
        value={formatMoney(sales.averageTicket)}
        note={
          sales.previous
            ? tStats("kpi.versus", { amount: formatMoney(sales.previous.averageTicket) })
            : undefined
        }
      />
      <Kpi
        testId="stats-kpi-transactions"
        label={tStats("kpi.transactions")}
        value={String(sales.transactionCount)}
        note={
          sales.previous ? tStats("kpi.versus", { amount: String(sales.previous.transactionCount) }) : undefined
        }
      />
      {/* Only when the window actually costed something. A restaurant with no
          recipes has no margin, and a zero here would report free food. */}
      {mix.margin !== null && mix.marginRatio !== null ? (
        <Kpi
          testId="stats-kpi-margin"
          label={tStats("kpi.margin")}
          value={formatShare(mix.marginRatio)}
          note={tStats("kpi.food_cost", { ratio: formatShare(mix.foodCostRatio) })}
        />
      ) : null}
    </section>
  )
}
